var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
// External dependencies
import { useState, useEffect, useContext } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableRow, Grid, Checkbox, TablePagination, TableHead, } from '@mui/material';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ArrowDropDown } from '@mui/icons-material';
import { useOutletContext } from 'react-router-dom';
// Internal dependencies
import { axiosClient } from '../axios';
import { getAuthHeader } from '../auth';
import { UserContext } from '../auth';
import { MULTILANGUAGE, LOG_LEVELS, SERVICES, ENDPOINTS, } from '../constants';
import { brandVoicesStyles } from './BrandVoices.styles';
;
/**
 * BrandVoices element
 * @param props Brand voices props
 * @returns Brand Voices element
 */
export const BrandVoicesTable = function (props) {
    // Get translation
    const { t, i18n } = useTranslation();
    // Extract output language
    const { outputLanguage } = useOutletContext();
    const { setSelectedBrandVoice, deleteBrandVoiceIds, setDeleteBrandVoiceIds, reloadBrandVoicesFlag, openBrandVoiceDialog, } = props;
    // BrandVoices list
    const [brandVoices, setBrandVoices] = useState([]);
    // Get user credential
    const user = useContext(UserContext);
    // Total brandVoices
    const [total, setTotal] = useState(0);
    // Page, limit
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    // Handle delete checkbox change
    const handleCheckboxChange = (event, _id) => {
        try {
            // Get checked
            const checked = event.target.checked;
            if (checked) {
                // Add delete item
                deleteBrandVoiceIds.push(_id);
            }
            else {
                // Remove delete item
                const deleteIdx = deleteBrandVoiceIds.indexOf(_id);
                deleteBrandVoiceIds.splice(deleteIdx, 1);
            }
            ;
            // Update delete Brand Voice ids
            setDeleteBrandVoiceIds([...deleteBrandVoiceIds]);
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `BrandVoices, handleCheckboxChange, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            toast.error(t('Something went wrong. We are figuring it out.'));
        }
        ;
    };
    // Get brandVoices when user credential, page, limit, deleteCount changed
    useEffect(() => {
        const getBrandVoices = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                // Get auth header
                const authHeader = yield getAuthHeader(user);
                const headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
                // Get user's brandVoices
                const res = yield axiosClient.get(`${process.env.SERVER_URL}${ENDPOINTS.CHAT}${ENDPOINTS.GET_BRAND_VOICES}?page=${page}&limit=${limit}`, { headers });
                // Extract total, brandVoices
                const { brandVoices, total } = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.data;
                // Update total, brandVoices
                setBrandVoices(brandVoices);
                setTotal(total);
            }
            catch (err) {
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `BrandVoices, getBrandVoices, ${err === null || err === void 0 ? void 0 : err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        service: SERVICES.AI_CHAT,
                        userId: user === null || user === void 0 ? void 0 : user.uid,
                        email: user === null || user === void 0 ? void 0 : user.email,
                        userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                    },
                });
            }
            ;
        });
        try {
            if (user) {
                getBrandVoices();
            }
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `BrandVoices, useEffect, getBrandVoices, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
        }
        ;
    }, [user, page, limit, reloadBrandVoicesFlag]);
    /**
     * Handle page change
     * @param {any} event
     * @param {number} value
     */
    const handlePageChange = (event, value) => {
        setPage(value);
    };
    /**
     * Handle limit change
     * @param {any} event
     */
    const handleLimitChange = (event) => {
        setLimit(event.target.value);
    };
    let dateFormat = 'DD MMM YYYY';
    switch (i18n.language) {
        case MULTILANGUAGE.en.value:
            dateFormat = 'DD MMM YYYY';
            break;
        case MULTILANGUAGE.ja.value:
            dateFormat = 'YYYY年M月D日';
            break;
        case MULTILANGUAGE.ko.value:
            dateFormat = 'YYYY년 M월 D일';
            break;
        case MULTILANGUAGE['zh-CN'].value:
            dateFormat = 'YYYY年M月D日';
            break;
        case MULTILANGUAGE['zh-HK'].value:
            dateFormat = 'YYYY年M月D日';
            break;
        case MULTILANGUAGE.id.value:
            dateFormat = 'DD MMMM YYYY';
            break;
        case MULTILANGUAGE.th.value:
            dateFormat = 'DD MMMM YYYY';
            break;
        case MULTILANGUAGE.vi.value:
            dateFormat = 'DD MMMM YYYY';
            break;
    }
    ;
    return (_jsxs(_Fragment, { children: [_jsx(TableContainer, Object.assign({ sx: { padding: '0' } }, { children: _jsxs(Table, Object.assign({ "aria-label": "table" }, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, Object.assign({ sx: Object.assign(Object.assign({}, brandVoicesStyles.cell), { padding: '0.875rem 1rem', width: '33%' }) }, { children: t('NAME') })), _jsx(TableCell, Object.assign({ sx: Object.assign(Object.assign({}, brandVoicesStyles.cell), { padding: '0.875rem 1rem', width: '50%' }) }, { children: t('BRAND VOICE') })), _jsx(TableCell, Object.assign({ sx: {
                                            alignItems: 'center',
                                            padding: '0.875rem 1rem',
                                        } }, { children: _jsxs(Box, Object.assign({ sx: { display: 'flex', alignItems: 'center' } }, { children: [_jsx(Typography, Object.assign({ sx: Object.assign({}, brandVoicesStyles.cell) }, { children: t('CREATED AT') })), _jsx(ArrowDropDown, { sx: { color: '#191C1A' } })] })) }))] }) }), _jsx(TableBody, { children: brandVoices.map((brandVoice) => {
                                // Document data
                                const { _id, name, content, createdAt } = brandVoice;
                                return (_jsxs(TableRow, Object.assign({ hover: true, sx: { cursor: 'pointer' }, onClick: () => {
                                        //edit brand voice
                                        setSelectedBrandVoice(brandVoice);
                                        openBrandVoiceDialog();
                                    } }, { children: [_jsx(TableCell, Object.assign({ sx: { padding: '0 0 0 1rem' } }, { children: _jsxs(Grid, Object.assign({ container: true, sx: { alignItems: 'center' }, columnSpacing: 1 }, { children: [_jsx(Grid, Object.assign({ item: true }, { children: _jsx(Checkbox, { sx: {
                                                                padding: '0',
                                                                color: '#191C1A',
                                                            }, onClick: (event) => {
                                                                event.stopPropagation();
                                                                handleCheckboxChange(event, _id);
                                                            } }) })), _jsx(Grid, Object.assign({ item: true, xs: true, sx: {
                                                            maxWidth: 'calc(100%)',
                                                            overflow: 'hidden',
                                                            width: '1px',
                                                            display: 'flex',
                                                        } }, { children: _jsx(Typography, Object.assign({ variant: 'text14', sx: Object.assign(Object.assign({}, brandVoicesStyles.cell), { textAlign: 'left', color: '#000000', marginLeft: '0.5rem', overflow: "hidden", textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) }, { children: name })) }))] })) })), _jsx(TableCell, Object.assign({ sx: { padding: '0 0 0 1rem' } }, { children: _jsx(Grid, Object.assign({ container: true }, { children: _jsx(Grid, Object.assign({ item: true, xs: 12, sx: {
                                                        overflow: 'hidden',
                                                        display: 'flex',
                                                        width: '1px',
                                                    } }, { children: _jsx(Typography, Object.assign({ variant: 'text14', sx: Object.assign(Object.assign({}, brandVoicesStyles.cell), { padding: '0.875rem 1rem 0.875rem 0', overflow: "hidden", textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', color: '#4B635A', textAlign: 'left' }) }, { children: content })) })) })) })), _jsx(TableCell, Object.assign({ sx: Object.assign(Object.assign({}, brandVoicesStyles.cell), { padding: '0.875rem 1rem' }) }, { children: _jsx(Typography, Object.assign({ variant: "text14", sx: { whiteSpace: 'nowrap', } }, { children: moment.utc(createdAt).local().locale(i18n.language).format(dateFormat) })) }))] }), _id));
                            }) })] })) })), _jsx(Box, Object.assign({ sx: {
                    width: '100%',
                    textAlign: 'right',
                    justifyContent: 'flex-end',
                } }, { children: _jsx(TablePagination, { component: "div", count: total, page: page, onPageChange: handlePageChange, rowsPerPage: limit, onRowsPerPageChange: handleLimitChange, labelRowsPerPage: t('Results'), labelDisplayedRows: ({ from, to, count }) => (`${from}–${to} ${t('of')} ${count !== -1 ? count : `more than ${to}`}`) }) }))] }));
};
